import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { BodyPrimary, BodySecondary, H2, LabelMini } from '@liveauctioneers/hammer-ui-core/text';
import { TextInput } from '@liveauctioneers/hammer-ui-core/textInput';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useEffect, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';

import { ApprovePhoneNumber } from '@/components/ApprovePhoneNumber/ApprovePhoneNumber';
import { BidderAddressList } from '@/components/BidderAddressList/BidderAddressList';
import { ProfileBasicsData, ProfileBasicsForm } from '@/components/ProfileBasicsForm/ProfileBasicsForm';
import { ResellerCheckbox } from '@/components/ResellerCheckbox/ResellerCheckbox';
import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import { PaymentProviders } from '@/enums';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useIsTabletLayout } from '@/hooks/useIsTabletLayout';
import { usePrevious } from '@/hooks/usePrevious';
import { SecurityForm } from '@/pages/AccountSettings/components/SecurityForm/SecurityForm';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    fetchBankAccountsIfNeeded,
    removeBankAccount,
} from '@/redux/modules/account/payments/bankAccount/bankAccount.actions';
import {
    getBankAccounts,
    getBankAccountsLoaded,
    getDefaultBankAccount,
} from '@/redux/modules/account/payments/bankAccount/bankAccount.selectors';
import { getUiUpdateAccount } from '@/redux/modules/account/updateAccount/updateAccount';
import { updateAccountDetails } from '@/redux/modules/account/updateAccount/updateAccount.actions';
import {
    getBidderMobileVerified,
    getBidderPhoneNumber,
    getUserData,
} from '@/redux/modules/account/user/user.selectors';
import { openChangePasswordModal, openDeleteAccountConfirmationModal } from '@/redux/modules/modal';
import { getProfileAddress } from '@/redux/modules/userAddress';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { preventDefault } from '@/utils/preventDefault';

import { BankAccountOnFileWrapper } from './components/BankAccountOnFileWrapper';
import { CardManagement } from './components/CardManagement';

export const ProfileForm = () => {
    const formatMessage = useAppFormatMessage();
    const dispatch = useAppDispatch();
    const isMobile = useIsMobileLayout();
    const isTablet = useIsTabletLayout();
    const { addresses, email, username } = useAppSelector(getUserData);
    const { submitted: editProfileSubmitted } = useAppSelector(getUiUpdateAccount);
    const profileAddress = useAppSelector(getProfileAddress);
    const bidderPhoneNumber = useAppSelector(getBidderPhoneNumber);
    const profileBasicsInit: ProfileBasicsData = {
        email,
        firstName: profileAddress.firstName,
        lastName: profileAddress.lastName,
        mobileNumber: bidderPhoneNumber,
    };
    const [profileBasicsForm, setProfileBasicsForm] = useState<ProfileBasicsData>(profileBasicsInit);
    const prevProfileAddress = usePrevious(profileAddress);
    const [profileBasicsFormValid, setProfileBasicsFormValid] = useState(true);
    const [profileBasicsFormSubmittable, setProfileBasicsFormSubmittable] = useState(false);

    const mobileVerified = useAppSelector(getBidderMobileVerified);
    const defaultBankAccount = useAppSelector((state) => getDefaultBankAccount(state, PaymentProviders.Payrix));
    const bankAccounts = useAppSelector(getBankAccounts);
    const bankAccountsLoaded = useAppSelector(getBankAccountsLoaded);

    const bankAccountToDisplay = defaultBankAccount || bankAccounts[bankAccounts.length - 1];

    const onBankAccountDelete = (bankAccountId: number, plaidAccountId?: string) => {
        dispatch(removeBankAccount({ bankAccountId, plaidAccountId }));
    };

    const onProfileBasicsChange = (valid: boolean, form: ProfileBasicsData) => {
        if (!isEqual(form, profileBasicsForm)) {
            setProfileBasicsForm(form);
            setProfileBasicsFormValid(valid);
            setProfileBasicsFormSubmittable(valid);
        }
    };

    const submitEditProfile = () => {
        const { email, firstName, lastName, mobileNumber } = profileBasicsForm;

        if (!profileBasicsFormValid) {
            return;
        }

        dispatch(
            updateAccountDetails({
                email,
                firstName,
                lastName,
                mobile: mobileNumber.replace(/[^\d]+/g, ''),
                zipcode: profileAddress.zipcode,
            })
        );

        setProfileBasicsFormSubmittable(false);
    };

    useEffect(() => {
        if (!isEqual(profileAddress, prevProfileAddress)) {
            setProfileBasicsForm({ ...profileBasicsForm, ...profileAddress });
        }
    }, [prevProfileAddress, profileAddress, profileBasicsForm]);

    useEffect(() => {
        if (!bankAccountsLoaded) {
            dispatch(fetchBankAccountsIfNeeded());
        }
    }, [bankAccountsLoaded, dispatch]);

    const openChangeModal = useCallback(() => dispatch(openChangePasswordModal()), [dispatch]);

    const openDeleteAccountModal = useCallback(() => dispatch(openDeleteAccountConfirmationModal()), [dispatch]);

    const spacerClass = useMemo(
        () => cn('mt-lg h-px w-full border-b border-border-secondary', isTablet ? 'mb-xl' : 'mb-2xl'),
        [isTablet]
    );

    return (
        <div className={cn('mt-md w-full', isMobile ? 'mb-0' : 'mb-3xl')}>
            <H2
                className={cn('mb-lg', { 'mb-sm': isTablet })}
                data-testid="myProfileTitle"
            >
                <FormattedMessage id="my_profile" />
            </H2>
            {/* no direct hammer-ui token for grey100 (#656565) — using text-text-secondary as closest match */}
            <div className="m-0 flex flex-col items-stretch justify-start p-0 leading-none text-text-secondary [&>p]:m-0">
                <div className={cn('flex', isTablet ? 'flex-col-reverse' : 'flex-row')}>
                    <ProfileBasicsForm
                        className={cn(isTablet ? 'w-full' : 'w-[583px]')}
                        formData={profileBasicsForm}
                        mobileVerified={mobileVerified}
                        updateParentForm={onProfileBasicsChange}
                    />
                    {!mobileVerified && (
                        <div className={cn(isTablet ? 'mx-0 mt-sm mb-md' : '-mt-2xl ml-md')}>
                            <ApprovePhoneNumber />
                        </div>
                    )}
                </div>
                <div
                    className="mt-xs flex flex-row items-center"
                    data-testid="profileForm"
                    onSubmit={preventDefault}
                >
                    {/* no direct hammer-ui token for grey300 (#a2a3a5) */}
                    <TextInput
                        aria-label={String(formatMessage(['password']))}
                        autoComplete="off"
                        className="mr-md w-full max-w-[583px] [&_input]:text-[#a2a3a5]"
                        data-testid="accountSettingsPassword"
                        disabled
                        id="password"
                        labelText={formatMessage(['password'])}
                        type="password"
                        value="**********"
                    />
                    <InlineButton
                        className="-mt-sm"
                        data-testid="changePasswordLink"
                        onClick={openChangeModal}
                        size="sm"
                    >
                        {formatMessage(['change_password'])}
                    </InlineButton>
                </div>
                <ResellerCheckbox className="mb-lg block" />
                <LabelMini className="mb-2xs">
                    <FormattedMessage id="accountSettings.username" />
                </LabelMini>
                <div className="mb-lg">
                    <BodySecondary>{username}</BodySecondary>
                </div>

                <div>
                    <ShouldLogInContainer runFunction={submitEditProfile}>
                        <Button
                            className="mt-md w-[194px]"
                            data-testid="myProfileSaveButton"
                            disabled={editProfileSubmitted || !profileBasicsFormValid || !profileBasicsFormSubmittable}
                            loading={editProfileSubmitted}
                        >
                            {formatMessage(['addresses', 'save'])}
                        </Button>
                    </ShouldLogInContainer>
                </div>
                <div className="mt-2xl">
                    <SecurityForm />
                </div>
                {/* This id is used to navigate users from the email url, directly to the payment information */}
                <div
                    className="mt-2xl"
                    id="card-management"
                >
                    <CardManagement />
                </div>
                <LabelMini className="my-xs">
                    <FormattedMessage id="accountSettings.bankAccountOnFileLabel" />
                </LabelMini>
                <BankAccountOnFileWrapper
                    bankAccountToDisplay={bankAccountToDisplay}
                    onBankAccountDelete={onBankAccountDelete}
                />
                <div className={spacerClass} />
                <H2 className="mb-sm">
                    <FormattedMessage id="address" />
                </H2>
                <BidderAddressList
                    addresses={addresses}
                    canEdit
                    setDefaultOnSelect
                    showRemoveButton
                    showSetDefaultButton
                    showTitle
                />
                <div className={spacerClass} />
                <H2 className="mb-sm">
                    <FormattedMessage id="accountSettings.deleteAccount" />
                </H2>
                <BodyPrimary>
                    <FormattedMessage id="accountSettings.deleteAccountWarning" />
                </BodyPrimary>
                <Button
                    className="mt-6 w-full mdMin:max-w-min"
                    data-testid="deleteAccountLink"
                    onClick={openDeleteAccountModal}
                    style="critical"
                    variant="outlined"
                >
                    {formatMessage(['accountSettings', 'delete'])}
                </Button>
            </div>
        </div>
    );
};
